import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import InputText from "../../../../components/form-control/input-text";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from 'primereact/card';
import { Skeleton } from "primereact/skeleton";
import apiFunctions from "../../../../services/api-service";
import useToast from "../../../../components/common/useToast";
import { AxiosError } from "axios";
import { errorHandler } from "../../../../utils/HelperFunctions";
import { persistor } from "../../../../redux/store";
import { logout } from "../../../../redux/features/authSlice";
import { useDispatch } from "react-redux";
import { ArrowLeftIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { ProgressSpinner } from "primereact/progressspinner";
import BhavSheetsPreview, { BhavSheet } from "./BhavSheetsPreview";

const BazarBhavCreate: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(!!id);
    // Live link preview: the backend parses the page the same way the mobile
    // app's price screen does — if it finds price tables the admin sees the
    // real table right here; if not, the link is flagged as invalid.
    const [previewState, setPreviewState] = useState<"idle" | "loading" | "valid" | "invalid">("idle");
    const [previewSheets, setPreviewSheets] = useState<BhavSheet[]>([]);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Name is required!"),
        link: Yup.string().required("Link is required!"),
    });

    const initialValue = {
        name: "",
        link: ""
    };

    const formik = useFormik({
        initialValues: initialValue,
        validationSchema: validationSchema,

        onSubmit: async (values: any) => {
            if (previewState === "invalid") {
                showToast({
                    severity: "error",
                    summary: "Invalid Link",
                    detail: "આ link માં ભાવ નું table મળ્યું નથી — valid link નાખો.",
                    life: 4000,
                });
                return;
            }
            let payload = {
                name: values.name,
                link: values.link,
            }
            if (id) {
                await apiFunctions.put(`/bazarbhav/editbazarbhav/${id}`, payload)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            showToast({
                                severity: "success",
                                summary: "Success",
                                detail: response.data.message,
                                life: 3000,
                            });
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        if (err.status === 401) {
                            dispatch(logout());
                            persistor.purge();
                            navigate("/admin/login");
                        }
                        errorHandler(err);
                    });
            } else {
                await apiFunctions.post(`/bazarbhav/addbazarbhav`, payload)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            showToast({
                                severity: "success",
                                summary: "Success",
                                detail: response.data.message,
                                life: 3000,
                            });
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        if (err.status === 401) {
                            dispatch(logout());
                            persistor.purge();
                            navigate("/admin/login");
                        }
                        errorHandler(err);
                    });
            }
        },
    });

    const getSubcastData = async (id: string) => {
        try {
            let url = `/bazarbhav/${id}`;
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        formik.setFieldValue("name", response.data.data.name);
                        formik.setFieldValue("link", response.data.data.link);
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (id) {
            getSubcastData(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced: fetch the parsed price tables whenever the link changes.
    useEffect(() => {
        const link = formik.values.link.trim();
        if (!/^https?:\/\/.+\..+/i.test(link)) {
            setPreviewState("idle");
            setPreviewSheets([]);
            return;
        }
        setPreviewState("loading");
        const timer = setTimeout(async () => {
            try {
                const response = await apiFunctions.get(`/bazarbhav/previewlink?link=${encodeURIComponent(link)}`);
                if (response?.data?.valid && response.data.sheets?.length) {
                    setPreviewSheets(response.data.sheets);
                    setPreviewState("valid");
                } else {
                    setPreviewSheets([]);
                    setPreviewState("invalid");
                }
            } catch {
                setPreviewSheets([]);
                setPreviewState("invalid");
            }
        }, 700);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.link]);

    const handleBack = () => {
        navigate('/admin/bazarbhav');
    }

    return (
        <>
            <div className="back-button" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>
            <div className="page-title">
                {id ? (<h3>Edit Bazar Bhav</h3>) : (<h3>Add Bazar Bhav</h3>)}
            </div>
            <form action="submit" onSubmit={formik.handleSubmit}>
                <Card className="grid form-layout">
                    {loading ? (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="60px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="60px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="field mt-5 mb-4">
                                    <Skeleton width="90px" height="40px" />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="lg:col-span-6 col-span-12">
                                    <InputText
                                        type="text"
                                        label="Name"
                                        placeholder=""
                                        modelValue={formik.values.name}
                                        disabled={false}
                                        autofocus={true}
                                        id="name"
                                        validation={
                                            formik.errors.name &&
                                            formik.touched.name && {
                                                $error: true,
                                                required: {
                                                    $invalid: true,
                                                    $message: formik.errors.name,
                                                },
                                            }
                                        }
                                        onUpdateModelValue={function (value: string): void {
                                            formik.setFieldValue("name", value);
                                        }}
                                    />
                                </div>
                                <div className="lg:col-span-6 col-span-12">
                                    <InputText
                                        type="text"
                                        label="Link"
                                        placeholder=""
                                        modelValue={formik.values.link}
                                        disabled={false}
                                        autofocus={false}
                                        id="link"
                                        validation={
                                            formik.errors.link &&
                                            formik.touched.link && {
                                                $error: true,
                                                required: {
                                                    $invalid: true,
                                                    $message: formik.errors.link,
                                                },
                                            }
                                        }
                                        onUpdateModelValue={function (value: string): void {
                                            formik.setFieldValue("link", value);
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Live table preview — parsed from the link by the backend
                                (same parser the mobile app's price screen uses) */}
                            {previewState !== "idle" && (
                                <div className="grid grid-cols-12 gap-4 mt-4">
                                    <div className="col-span-12">
                                        <label className="form-label">Bhav Preview</label>
                                        <div
                                            style={{
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "8px",
                                                marginTop: "6px",
                                                background: "#fff",
                                                padding: "12px",
                                                maxHeight: "480px",
                                                overflowY: "auto",
                                            }}
                                        >
                                            {previewState === "loading" && (
                                                <div className="flex items-center gap-3 p-3">
                                                    <ProgressSpinner style={{ width: "28px", height: "28px" }} strokeWidth="5" />
                                                    <span style={{ color: "#6b7280" }}>Checking link...</span>
                                                </div>
                                            )}
                                            {previewState === "invalid" && (
                                                <div className="flex items-center gap-2 p-3" style={{ color: "#b91c1c" }}>
                                                    <ExclamationTriangleIcon style={{ width: "22px", height: "22px", flexShrink: 0 }} />
                                                    <span>
                                                        આ link valid નથી — page માં ભાવ નું table મળ્યું નથી. Please khedutbhai.com નું APMC ભાવ page ની link નાખો.
                                                    </span>
                                                </div>
                                            )}
                                            {previewState === "valid" && <BhavSheetsPreview sheets={previewSheets} />}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-12 gap-4">
                                <div className="field mt-5 mb-4">
                                    {id ? (
                                        <Button
                                            label="Edit"
                                            className="prime-button btn-login w-max"
                                            type="submit"
                                        ></Button>
                                    ) : (
                                        <Button
                                            label="ADD"
                                            className="prime-button btn-login w-max"
                                            type="submit"
                                        ></Button>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </Card>
            </form >
        </>
    );
};

export default BazarBhavCreate;
